import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getAllSimpleStaffs, deleteSimpleStaff, deleteAllSimpleStaffs } from '../../../redux/staffRelated/staffHandle';
import { exportToExcel, getCurrentDateString } from '../../../utils/excelExport';
import {
    Box, Typography, Paper, Container, Chip, Stack, Avatar,
    CircularProgress, Divider, Grid, IconButton, Button, Tooltip
} from '@mui/material';
import {
    FileDownload as FileDownloadIcon,
    UploadFile as UploadFileIcon,
    PersonRemove as PersonRemoveIcon,
    Person as PersonIcon,
    Add as AddIcon,
    Group as GroupIcon,
    Visibility as VisibilityIcon,
    AddCircle as AddCircleIcon,
    DeleteSweep as DeleteSweepIcon,
    PostAdd as PostAddIcon
} from '@mui/icons-material';
import { GreenButton } from '../../../components/buttonStyles';
import { useToast } from '../../../context/ToastContext';
import TableTemplate from '../../../components/TableTemplate';
import styled, { keyframes } from 'styled-components';
import * as XLSX from 'xlsx';

const ShowStaff = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { staffList, loading, error, response } = useSelector((state) => state.staff);
    const { currentUser } = useSelector((state) => state.user);

    const fileInputRef = useRef(null);
    const [importing, setImporting] = useState(false);
    const { showToast } = useToast();

    useEffect(() => {
        dispatch(getAllSimpleStaffs(currentUser._id));
    }, [currentUser._id, dispatch]);

    const handleStaffExport = () => {
        if (!Array.isArray(staffList) || staffList.length === 0) return;
        const exportData = staffList.map((staff) => ({
            'Name': staff.name,
            'Position': staff.position || 'N/A',
            'Phone': staff.phone || 'N/A',
            'Address': staff.address || 'N/A',
        }));
        exportToExcel(exportData, `Staff_List_${getCurrentDateString()}`, 'Staff List');
    };

    const handleStaffImportFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const rows = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], { defval: '' });

                if (!rows.length) {
                    showToast('File contains no data.', 'warning');
                    return;
                }

                const staffMembers = rows.map(row => ({
                    name: row.Name?.toString().trim(),
                    position: row.Position?.toString().trim(),
                    phone: row.Phone?.toString().trim() || '',
                    address: row.Address?.toString().trim() || ''
                }));

                setImporting(true);
                const response = await fetch(`${process.env.REACT_APP_BASE_URL}/SimpleStaffs/BulkCreate`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ school: currentUser._id, staffMembers })
                });
                const result = await response.json();

                showToast(result.message || `Imported ${result.createdCount} staff members.`, 'success');
                dispatch(getAllSimpleStaffs(currentUser._id));
            } catch (err) {
                showToast('Failed to import staff.', 'error');
            } finally {
                setImporting(false);
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const deleteHandler = (deleteID) => {
        dispatch(deleteSimpleStaff(deleteID)).then(() => {
            dispatch(getAllSimpleStaffs(currentUser._id));
            showToast("Staff member removed successfully", "success");
        });
    };

    const deleteAllHandler = () => {
        dispatch(deleteAllSimpleStaffs(currentUser._id)).then(() => {
            dispatch(getAllSimpleStaffs(currentUser._id));
            showToast("All staff records removed", "info");
        });
    };

    const columns = [
        {
            id: 'photo', label: 'Photo', minWidth: 80, render: (photo) => (
                <Avatar src={photo ? `${process.env.REACT_APP_BASE_URL}/${photo}` : ''} sx={{ bgcolor: '#667eea' }}><PersonIcon /></Avatar>
            )
        },
        { id: 'name', label: 'Name', minWidth: 150 },
        {
            id: 'position', label: 'Position', minWidth: 120, render: (pos) => (
                <Chip label={pos} size="small" sx={{ bgcolor: '#f0f4ff', color: '#667eea', fontWeight: 600 }} />
            )
        },
        { id: 'phone', label: 'Phone', minWidth: 120 },
        { id: 'address', label: 'Address', minWidth: 150 },
    ];

    const rows = (staffList || []).map((staff) => ({
        photo: staff.photo,
        name: staff.name,
        address: staff.address || '-',
        position: staff.position || '-',
        phone: staff.phone || '-',
        id: staff._id,
    }));

    const StaffButtonHaver = ({ row }) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Tooltip title="View Details">
                <IconButton onClick={() => navigate("/Admin/staff/" + row.id)} sx={{ bgcolor: '#f0f4ff', color: '#667eea' }} size="small">
                    <VisibilityIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete Staff">
                <IconButton onClick={() => deleteHandler(row.id)} sx={{ bgcolor: '#fff1f2', color: '#f43f5e' }} size="small">
                    <PersonRemoveIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );



    if (loading) {
        return (
            <LoadingWrapper>
                <CircularProgress size={60} />
                <Typography sx={{ mt: 2, color: '#667eea', fontWeight: 600 }}>Loading Staff Records...</Typography>
            </LoadingWrapper>
        );
    }

    return (
        <PageContainer>
            <StickyHeader elevation={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2, flexWrap: 'wrap', gap: 2 }}>
                    <HeaderIconWrapper><GroupIcon sx={{ fontSize: 32 }} /></HeaderIconWrapper>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Staff Management</Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>Oversee institution personnel and roles</Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    <Stack direction="row" spacing={1}>
                        <GreenButton variant="contained" onClick={() => navigate("/Admin/addstaff")} startIcon={<AddCircleIcon />} sx={{ borderRadius: '12px' }}>
                            Add Staff
                        </GreenButton>
                        <Button variant="outlined" onClick={() => fileInputRef.current?.click()} startIcon={<UploadFileIcon />} sx={{ borderRadius: '12px', color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}>
                            Import
                        </Button>
                    </Stack>
                </Box>
            </StickyHeader>

            <MainContent maxWidth="xl">
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={4}>
                        <StatCard elevation={2} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                            <StatIconWrapper><GroupIcon /></StatIconWrapper>
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="h6" sx={{ opacity: 0.8, fontSize: '0.8rem' }}>TOTAL STAFF</Typography>
                                <Typography variant="h4" sx={{ fontWeight: 800 }}>{rows.length}</Typography>
                            </Box>
                        </StatCard>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <Paper sx={{ p: 2.5, borderRadius: '20px', display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'white' }}>
                            <Tooltip title="Download Import Template">
                                <Button variant="text" startIcon={<PostAddIcon />} onClick={() => exportToExcel([{ Name: '', Position: '', Phone: '', Address: '' }], 'Staff_Template', 'Staff')}>Template</Button>
                            </Tooltip>
                            <Divider orientation="vertical" flexItem />
                            <Tooltip title="Export Current List">
                                <Button variant="text" startIcon={<FileDownloadIcon />} onClick={handleStaffExport}>Export Excel</Button>
                            </Tooltip>
                        </Paper>
                    </Grid>
                </Grid>

                <SectionCard elevation={2}>
                    <SectionHeader>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>Staff Directory</Typography>
                        <input type="file" accept=".xlsx, .xls" ref={fileInputRef} style={{ display: 'none' }} onChange={handleStaffImportFile} />
                    </SectionHeader>
                    <Divider sx={{ mb: 3 }} />

                    {rows.length === 0 ? (
                        <EmptyState>
                            <PersonIcon sx={{ fontSize: 64, color: '#e5e7eb', mb: 2 }} />
                            <Typography variant="h6" color="textSecondary">No Staff Records</Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>Start building your team by adding members.</Typography>
                            <GreenButton variant="contained" onClick={() => navigate("/Admin/addstaff")}>Add Staff Member</GreenButton>
                        </EmptyState>
                    ) : (
                        <TableTemplate buttonHaver={StaffButtonHaver} columns={columns} rows={rows} />
                    )}
                </SectionCard>
            </MainContent>

        </PageContainer>
    );
};

export default ShowStaff;

const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

const PageContainer = styled.div`background-color: #f8fafc; min-height: 100vh;`;
const StickyHeader = styled(Paper)`
    position: sticky; top: 0; z-index: 1000; border-radius: 0 !important;
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%) !important; color: white !important;
`;
const HeaderIconWrapper = styled(Box)`
    background: rgba(255, 255, 255, 0.1); width: 56px; height: 56px; border-radius: 14px;
    display: flex; align-items: center; justify-content: center;
`;
const MainContent = styled(Container)`padding-top: 32px; padding-bottom: 40px; animation: ${fadeIn} 0.5s ease-out;`;
const StatCard = styled(Paper)`
    padding: 24px; border-radius: 20px !important; color: white !important;
    display: flex; justify-content: space-between; align-items: center;
`;
const StatIconWrapper = styled(Box)`
    background: rgba(255, 255, 255, 0.2); width: 48px; height: 48px; border-radius: 12px;
    display: flex; align-items: center; justify-content: center; svg { font-size: 28px; }
`;
const SectionCard = styled(Paper)`padding: 32px; border-radius: 24px !important; background: white; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05) !important;`;
const SectionHeader = styled(Box)`display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;`;
const EmptyState = styled(Box)`display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 20px; text-align: center;`;
const LoadingWrapper = styled(Box)`display: flex; flex-direction: column; justify-content: center; align-items: center; height: 80vh;`;
